import React, { useEffect, useState } from "react";

import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";

import { StepItems, type SubItem } from "./UploadProgress";
import type { PlanFile, PlanProgressStep } from "../../Types/AccountPlan";

interface Props {
  file: PlanFile;
  /** "done": the finished run, read back on the Plan tab (product review 2026-11-01). */
  variant?: "live" | "done";
}

type RowStatus = "done" | "active" | "pending" | "failed";

interface Row {
  key: string;
  label: string;
  status: RowStatus;
  ms?: number;
  startedAt?: string;
  items?: SubItem[];
}

const rowStatus = (s?: PlanProgressStep): RowStatus =>
  s?.state === "done"
    ? "done"
    : s?.state === "active"
      ? "active"
      : s?.state === "failed"
        ? "failed"
        : "pending";

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** Items kept after the check against the slides, across every section. */
export const itemsChecked = (file: PlanFile): number =>
  (file.progress?.briefing?.steps?.verify?.items ?? []).reduce(
    (n, i) => n + (i.count ?? 0),
    0
  );

/**
 * "9 sections · 5 Briefing Questions answered · 33 items checked against the
 * slides": what a finished run produced, for the Plan tab's summary.
 */
export const briefingRunSummary = (file: PlanFile): string => {
  const steps = file.progress?.briefing?.steps;
  const sections = steps?.write?.items?.length ?? 0;
  const answered =
    file.sponsor_view?.briefing?.answers?.length ??
    steps?.questions?.items?.length ??
    0;
  const checked = itemsChecked(file);
  return [
    sections && plural(sections, "section"),
    answered &&
      `${answered} Briefing Question${answered === 1 ? "" : "s"} answered`,
    checked && `${plural(checked, "item")} checked against the slides`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Rows for the Review briefing checklist, from the server's progress.briefing
 * (backend progress.py, BriefingProgress). The briefing and the briefing
 * questions are two AI passes that really run side by side, so both can be in
 * progress at once; every kept item is then checked against its slide.
 */
export const briefingRows = (file: PlanFile): Row[] | null => {
  const steps = file.progress?.briefing?.steps;
  if (!steps) return null;
  const write = steps.write;
  const questions = steps.questions;
  const verify = steps.verify;
  const chat = steps.chat;
  const sections = write?.items?.length ?? 0;
  const asked = questions?.items?.length ?? 0;
  const checked = itemsChecked(file);
  const vN = `v${file.plan_version ?? file.version}`;
  const items = (s?: PlanProgressStep): SubItem[] | undefined =>
    s?.items?.map(i => ({
      label:
        i.count != null ? `${i.label} · ${plural(i.count, "item")}` : i.label,
      status: i.state === "failed" ? "failed" : i.state,
    }));
  return [
    {
      key: "write",
      label:
        write?.state === "done"
          ? `Wrote the briefing · ${plural(sections, "section")}`
          : sections
            ? `Writing the briefing · ${plural(sections, "section")}`
            : "Writing the briefing",
      status: rowStatus(write),
      ms: write?.ms,
      startedAt: write?.started_at,
      items: items(write),
    },
    {
      key: "questions",
      label:
        questions?.state === "done"
          ? `Answered the Briefing Questions · ${asked}`
          : asked
            ? `Answering the Briefing Questions · ${asked}`
            : "Answering the Briefing Questions",
      status: rowStatus(questions),
      ms: questions?.ms,
      startedAt: questions?.started_at,
      items: items(questions),
    },
    {
      key: "verify",
      label:
        verify?.state === "done" && checked
          ? `Checked ${plural(checked, "item")} against the slides they quote`
          : "Checking every item against the slide it quotes",
      status: rowStatus(verify),
      items: items(verify),
    },
    {
      // Chat answers from this version's slides, briefing and briefing-question
      // answers (with the suggested questions from that pass): ready the moment
      // the briefing is saved, which the server records in the same write.
      key: "chat",
      label:
        chat?.state === "done"
          ? `Prepared Ask about this plan for ${vN} · opens once published`
          : `Preparing Ask about this plan for ${vN}`,
      status: chat
        ? rowStatus(chat)
        : verify?.state === "done"
          ? "active"
          : "pending",
      items: chat
        ? items(chat)
        : verify?.state === "done"
          ? [
              "Answers to the briefing questions",
              "The briefing's sections",
              "The plan's slides",
            ].map(label => ({ label, status: "active" as const }))
          : undefined,
    },
    {
      key: "ready",
      label: "Ready for your review",
      status: file.sponsor_view_status === "ready" ? "done" : "pending",
    },
  ];
};

const seconds = (ms: number) =>
  ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;

/** Live checklist on Review briefing while the draft briefing is written. */
const BriefingProgress: React.FC<Props> = ({ file, variant = "live" }) => {
  const rows = briefingRows(file);
  const anyActive = !!rows?.some(r => r.status === "active");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [anyActive]);

  if (!rows) {
    // A briefing started before this checklist existed: the old one-line status.
    return (
      <div
        className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white py-14 text-center"
        role="status"
      >
        <Loader2 className="h-7 w-7 animate-spin text-[#974ae4]" aria-hidden />
        <div className="text-sm font-semibold text-gray-900">
          Writing the draft briefing…
        </div>
        <div className="text-xs text-gray-500">
          Usually under a minute. Every item is checked against its slide.
        </div>
      </div>
    );
  }

  const elapsed = (startedAt?: string) => {
    const t = startedAt ? Date.parse(startedAt) : NaN;
    return Number.isNaN(t) ? null : Math.max(0, now - t);
  };

  return (
    <div
      role={variant === "live" ? "status" : undefined}
      data-testid="briefing-progress"
      data-variant={variant}
      className={
        variant === "live"
          ? "rounded-xl border border-purple-200 bg-purple-50/40 px-4 py-3 text-sm text-gray-700"
          : "text-sm text-gray-700"
      }
    >
      {variant === "live" && (
        <>
          <div className="font-medium text-gray-800">
            Writing the draft briefing for v{file.plan_version ?? file.version}
          </div>
          <div className="text-xs text-gray-500">
            Usually under a minute. You can leave this page; it keeps going and
            you&apos;ll be notified when it&apos;s ready to review.
          </div>
        </>
      )}
      <ol
        className="mt-3 space-y-2 rounded-lg border border-gray-100 bg-white/70 p-3"
        aria-label="Briefing progress"
      >
        {rows.map(r => {
          const running = r.status === "active" ? elapsed(r.startedAt) : null;
          return (
            <li
              key={r.key}
              data-testid={`briefing-${r.key}`}
              data-status={r.status}
              className="flex flex-wrap items-start gap-x-2"
            >
              {r.status === "done" ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                  aria-label="done"
                />
              ) : r.status === "active" ? (
                <Loader2
                  className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#974ae4]"
                  aria-label="in progress"
                />
              ) : r.status === "failed" ? (
                <XCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
                  aria-label="stopped here"
                />
              ) : (
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
                  aria-label="pending"
                />
              )}
              <span
                className={`flex-1 ${
                  r.status === "pending"
                    ? "text-gray-400"
                    : r.status === "active"
                      ? "font-medium text-purple-800"
                      : r.status === "failed"
                        ? "text-red-700"
                        : "text-gray-800"
                }`}
              >
                {r.label}
              </span>
              {r.status === "done" && r.ms != null && (
                <span className="font-mono text-xs text-gray-400">
                  {seconds(r.ms)}
                </span>
              )}
              {running != null && (
                <span className="font-mono text-xs text-purple-700">
                  {Math.floor(running / 1000)} s…
                </span>
              )}
              {r.items && (
                <StepItems rowKey={`briefing-${r.key}`} items={r.items} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default BriefingProgress;
